// The Theropoda footprint alphabet.
//
// IT IS A SYSTEM, NOT A LOOKUP TABLE. This is the thing worth knowing, and it
// took reading the chart properly to see: the alphabet is completely regular.
//
//   A–D          one print, in each of four directions: down, right, up, left
//   E–T          two prints STACKED — all sixteen ordered pairs of those four
//   U–Z, 0–9    two prints SIDE BY SIDE — the same sixteen pairs again
//
// 4 + 16 + 16 = 36, which is twenty-six letters and ten digits exactly. The
// proof it is right: the four MATCHED pairs (down-down, right-right, up-up,
// left-left) must land on every fifth character, and they do — E J O T in the
// stacked set, U Z 4 9 in the side-by-side set. That is what the chart shows.
//
// So nothing here is a guess about individual letters. Get the four directions
// and the two orderings right and every character follows.
//
// THE DRAWINGS ARE ORIGINAL. The cipher is a system and that is what is
// reproduced; the foot below is drawn from scratch and traced from nothing.

import { computed } from 'vue';
import { alphabetLetters, addAlphabetLetter, storeReady } from './store.js';

const HEEL = 6;

// One toe: swells from the heel, pinches to a claw. FILLED, not stroked — an
// even-width line reads as a diagram rather than a print pressed into ground.
function toe(deg, len, hw) {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const P = (x, y) => {
    const dy = y - HEEL;
    return `${(x * c - dy * s).toFixed(2)},${(HEEL + x * s + dy * c).toFixed(2)}`;
  };
  return `M${P(-hw * 0.9, HEEL + 1.5)} C${P(-hw * 1.05, HEEL - len * 0.3)} ${P(-hw * 0.78, HEEL - len * 0.74)} ${P(-hw * 0.2, HEEL - len * 0.95)} L${P(0, HEEL - len)} L${P(hw * 0.2, HEEL - len * 0.95)} C${P(hw * 0.78, HEEL - len * 0.74)} ${P(hw * 1.05, HEEL - len * 0.3)} ${P(hw * 0.9, HEEL + 1.5)} Z`;
}

/** The one foot. Middle toe longest — without that it reads as a flower. */
export const FOOT = [toe(-46, 13.5, 2.6), toe(0, 17, 2.8), toe(46, 13.5, 2.6)].join(' ');

// Rotations, named. The foot as built points DOWN at rotate(0), so these are
// measured rather than assumed — getting this backwards mirrors the whole
// alphabet, which is exactly the bug that showed up the first time.
export const DOWN = 0;
export const LEFT = 90;
export const UP = 180;
export const RIGHT = 270;

/** The order A B C D introduce the four directions. */
const DIRS = [DOWN, RIGHT, UP, LEFT];

const SINGLES = 'ABCD';
const STACKED = 'EFGHIJKLMNOPQRST';
const BESIDE = 'UVWXYZ0123456789';
export const CHARACTERS = SINGLES + STACKED + BESIDE;

export const STACK = 30;
export const SIDE = 22;
export const SCRIPTS = ['theropoda', 'transitional', 'sawtooth', 'ceratopsia'];

/** The whole alphabet, derived rather than typed out. */
export const TABLE = (() => {
  const t = {};
  [...SINGLES].forEach((ch, i) => {
    t[ch] = { character: ch, layout: 'single', bearing: DIRS[i], bearingTwo: null };
  });
  [...STACKED].forEach((ch, k) => {
    t[ch] = { character: ch, layout: 'stacked', bearing: DIRS[Math.floor(k / 4)], bearingTwo: DIRS[k % 4] };
  });
  [...BESIDE].forEach((ch, k) => {
    t[ch] = { character: ch, layout: 'beside', bearing: DIRS[Math.floor(k / 4)], bearingTwo: DIRS[k % 4] };
  });
  return t;
})();

const kindOf = (ch) => (/[0-9]/.test(ch) ? 'digit' : 'letter');

/** Write the table into the store once, so the app has data to read. */
export async function ensureAlphabet(script = 'theropoda') {
  await storeReady;
  if (script !== 'theropoda') return;   // the other three are not entered yet
  const have = alphabetLetters.value.some((r) => Number(r.id) > 0 && r.script === script);
  if (have) return;
  for (const ch of CHARACTERS) {
    const t = TABLE[ch];
    addAlphabetLetter({
      script,
      character: ch,
      layout: t.layout,
      bearing: t.bearing,
      bearingTwo: t.bearingTwo ?? 0,
      isStar: false,
      kind: kindOf(ch),
    });
  }
}

/** Stored rows for one script. Demo rows (negative ids) are not the alphabet. */
export const lettersFor = (script) => computed(() => alphabetLetters.value
  .filter((r) => Number(r.id) > 0 && r.script === script)
  .sort((a, b) => CHARACTERS.indexOf(a.character) - CHARACTERS.indexOf(b.character)));

export const theropoda = lettersFor('theropoda');

/** The prints for one character: one, or two placed by layout. */
export function printsFor(row) {
  const t = row && row.layout ? row : TABLE[String(row || '').toUpperCase()];
  if (!t) return [];
  if (t.layout === 'stacked') {
    return [
      { d: FOOT, transform: `translate(0,${-STACK / 2}) rotate(${t.bearing})` },
      { d: FOOT, transform: `translate(0,${STACK / 2}) rotate(${t.bearingTwo})` },
    ];
  }
  if (t.layout === 'beside') {
    return [
      { d: FOOT, transform: `translate(${-SIDE / 2},0) rotate(${t.bearing})` },
      { d: FOOT, transform: `translate(${SIDE / 2},0) rotate(${t.bearingTwo})` },
    ];
  }
  return [{ d: FOOT, transform: `rotate(${t.bearing})` }];
}

/**
 * Text as a run of characters to draw. Spaces stay as gaps — a message with the
 * word breaks removed is a puzzle nobody asked for. A side-by-side character is
 * wider, so it carries its own advance rather than everything sitting on one
 * fixed pitch and colliding.
 */
export function encode(text) {
  return String(text || '').toUpperCase().split('').map((ch, i) => {
    if (ch === ' ') return { key: `s${i}`, kind: 'space', advance: 0.7 };
    const t = TABLE[ch];
    if (!t) return { key: `x${i}`, kind: 'literal', character: ch, advance: 0.7 };
    return {
      key: `g${i}`,
      kind: 'glyph',
      character: ch,
      prints: printsFor(t),
      advance: t.layout === 'beside' ? 1.5 : 1,
    };
  });
}

/** Footprints back into words, for the decode screen. */
export const decode = (chars) => (chars || []).join('');
